import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Clapperboard, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1612544409025-e1f6a56c1152?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await login(email, password); nav("/admin"); }
    catch (ex) { setErr(formatErr(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0B0F17] text-slate-100">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Studio" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/40 to-transparent" />
        <div className="absolute bottom-14 left-14 right-14 sv-rise">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-indigo-300 mb-4">Campaign video platform</p>
          <h2 className="font-display text-5xl font-bold leading-[1.05] tracking-tight max-w-lg">
            Every view, every email, every second — tracked.
          </h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 sv-grid-bg">
        <form onSubmit={submit} className="w-full max-w-sm sv-rise sv-d1" data-testid="admin-login-form">
          <div className="flex items-center gap-2.5 mb-12">
            <span className="w-9 h-9 rounded-md bg-indigo-500 grid place-items-center"><Clapperboard className="w-4 h-4" /></span>
            <span className="font-display font-bold text-xl tracking-tight">StreamVault</span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Admin sign in</h1>
          <p className="text-sm text-slate-400 mb-8">Manage campaigns, videos and leads.</p>
          <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Email</label>
          <input
            data-testid="admin-login-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-11 px-4 rounded-md bg-[#141A24] border border-[#242C3D] focus:border-indigo-500 focus:outline-none mb-5 transition-colors"
          />
          <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Password</label>
          <input
            data-testid="admin-login-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-11 px-4 rounded-md bg-[#141A24] border border-[#242C3D] focus:border-indigo-500 focus:outline-none mb-6 transition-colors"
          />
          {err && <p data-testid="admin-login-error" className="text-sm text-rose-400 mb-4">{err}</p>}
          <button
            data-testid="admin-login-submit-button" disabled={busy}
            className="w-full h-11 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
